import styled from 'styled-components';
import { motion } from 'framer-motion';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import DesignTokens from '../../styles/DesignTokens';

/**
 * Reusable glass-morphism card component (frosted glass with blur)
 */
export const GlassCard = styled(motion.div)`
  background: ${props => props.$variant === 'heavy'
    ? GlassmorphismTokens.glass.heavy
    : props.$variant === 'light'
      ? GlassmorphismTokens.glass.light
      : GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: ${DesignTokens.borderRadius['2xl']};
  box-shadow: ${GlassmorphismTokens.shadow.glass};
  color: white;
  padding: ${DesignTokens.spacing.xl};

  /* Fallback for browsers that don't support backdrop-filter */
  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.85);
  }
`;

/**
 * Full-bleed scenic background with dark overlay
 */
export const SceneBackground = styled.div`
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  background-image: url(${props => props.$bgImage || GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.scenicOverlay};
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

/**
 * Blue gradient CTA button replacing red GenerateButton
 */
export const BlueCTAButton = styled(motion.button)`
  width: 100%;
  padding: 1.125rem 2rem;
  background: ${props => props.disabled
    ? '#555'
    : GlassmorphismTokens.gradient.blueCTA};
  color: white;
  border: none;
  border-radius: ${DesignTokens.borderRadius.md};
  font-size: 1.0625rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  box-shadow: ${GlassmorphismTokens.shadow.blueCTA};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.blueCTAHover};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${GlassmorphismTokens.shadow.blueCTAHover};

    &::before {
      opacity: 1;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  span, svg {
    position: relative;
    z-index: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * Dark overlay base for screens 5-6 (activity detail, preferences)
 */
export const DarkModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${GlassmorphismTokens.dark.overlay};
  backdrop-filter: ${GlassmorphismTokens.blur.sm};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${DesignTokens.zIndex.modal};
  padding: ${DesignTokens.spacing.lg};
  overflow-y: auto;
`;

/**
 * Dark surface card for modals
 */
export const DarkSurfaceCard = styled(motion.div)`
  background: ${GlassmorphismTokens.dark.surface};
  border: ${GlassmorphismTokens.border.dark};
  border-radius: ${DesignTokens.borderRadius.xl};
  box-shadow: ${GlassmorphismTokens.shadow.dark};
  color: ${GlassmorphismTokens.dark.text};
  max-width: ${props => props.$maxWidth || '900px'};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

export default GlassCard;
